import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, X, Check } from "lucide-react";
import { blClient, adminClient } from "./client";

export function BLNotices({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [body, setBody] = useState("");
  const [author, setAuthor] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await blClient.get("/blackleaf/notices");
      setItems(r.data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    try {
      if (editing) {
        await adminClient.put(`/blackleaf/admin/notices/${editing}`, { body, author });
        toast.success("Notice updated");
      } else {
        await blClient.post("/blackleaf/notices", { body, author });
        toast.success("Notice posted");
      }
      setBody(""); setAuthor(""); setComposing(false); setEditing(null);
      await load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Couldn't post");
    }
  };

  const removeNotice = async (id) => {
    if (!window.confirm("Remove this notice?")) return;
    try {
      await adminClient.delete(`/blackleaf/admin/notices/${id}`);
      toast.success("Removed");
      await load();
    } catch (e2) {
      toast.error("Admin login required");
    }
  };

  const beginEdit = (n) => { setEditing(n.id); setBody(n.body); setAuthor(n.author || ""); setComposing(true); };

  return (
    <section className="bl-section" data-testid="bl-notices">
      <header className="bl-section-head">
        <div>
          <p className="bl-kicker">Society Notice Board</p>
          <h2 className="bl-h2">Announcements & news</h2>
        </div>
        {!composing && (
          <button onClick={() => { setComposing(true); setEditing(null); setBody(""); setAuthor(""); }} className="bl-btn" data-testid="bl-notice-add"><Plus size={14}/> Post a notice</button>
        )}
      </header>

      {composing && (
        <form onSubmit={submit} className="bl-card p-5 mb-5" data-testid="bl-notice-form">
          <div className="flex items-center justify-between mb-3">
            <strong style={{ color: "var(--bl-gold)" }}>{editing ? "Edit notice" : "New notice"}</strong>
            <button type="button" className="bl-btn-ghost" onClick={() => { setComposing(false); setEditing(null); }}><X size={12}/> Cancel</button>
          </div>
          <label className="block mb-3">
            <span className="text-xs uppercase tracking-[0.18em] text-bl-soft">Message</span>
            <textarea className="bl-field mt-1.5" rows={3} value={body} onChange={(e) => setBody(e.target.value)} required data-testid="bl-notice-body"/>
          </label>
          <label className="block mb-3">
            <span className="text-xs uppercase tracking-[0.18em] text-bl-soft">Your name (optional)</span>
            <input className="bl-field mt-1.5" value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Anonymous" data-testid="bl-notice-author"/>
          </label>
          <button type="submit" className="bl-btn" data-testid="bl-notice-submit"><Check size={14}/> {editing ? "Save changes" : "Pin it up"}</button>
        </form>
      )}

      {loading ? <Loader2 size={20} className="animate-spin mx-auto block text-bl-soft my-10"/> :
        items.length === 0 ? <div className="bl-card p-10 text-center text-bl-soft italic">The board is quiet. Be the first to post.</div> :
        <div className="space-y-3">
          {items.map((n) => (
            <article key={n.id} className="bl-card p-5 flex gap-3 items-start" data-testid={`bl-notice-${n.id}`}>
              <div className="flex-1">
                <p className="text-bl-ink whitespace-pre-wrap leading-relaxed">{n.body}</p>
                <p className="text-xs text-bl-soft italic mt-2">— {n.author || "Anonymous"} · {new Date(n.created_at).toLocaleDateString()}</p>
              </div>
              {isAdmin && (
                <div className="flex flex-col gap-1">
                  <button onClick={() => beginEdit(n)} className="bl-btn-ghost" data-testid={`bl-notice-edit-${n.id}`}><Pencil size={12}/></button>
                  <button onClick={() => removeNotice(n.id)} className="bl-btn-ghost" data-testid={`bl-notice-del-${n.id}`}><Trash2 size={12}/></button>
                </div>
              )}
            </article>
          ))}
        </div>
      }
    </section>
  );
}
